import { useEffect, useState } from "react";
import { getAttorneyDocuments, viewAttorneyDocument } from "../api/documents";

import AttorneyNav from "../components/AttorneyNav";
import StatusMessage from "../components/StatusMessage";
import { getErrorMessage } from "../utils/apiErrors";

function formatDate(value) {
  if (!value) return "—";

  return new Date(value).toLocaleString();
}

function formatStatus(status) {
  if (status === "submitted") return "Pending Review";
  if (status === "approved") return "Ready for Release";
  if (status === "released") return "Released";
  if (status === "rejected") return "Rejected";

  return status || "Unknown";
}

function statusClass(status) {
  if (status === "released") return "status-released";
  if (status === "approved" || status === "submitted") return "status-approved";
  if (status === "rejected") return "status-rejected";

  return "status-approved";
}

function scanStatus(status) {
  return ({ pending: "Security scan pending", scanning: "Security review in progress", clean: "Security scan complete", bypassed: "Legacy document", suspicious: "Blocked by security controls", infected: "Blocked by security controls", failed: "Security review unavailable" })[status] || "Security scan pending";
}

export default function AttorneyDocuments() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingId, setViewingId] = useState(null);
  const [error, setError] = useState("");
  const [securityReviewEnforced, setSecurityReviewEnforced] = useState(false);

  const loadDocuments = async () => {
    setLoading(true);

    try {
      const res = await getAttorneyDocuments();
      setDocuments(res.data?.data || []);
      setSecurityReviewEnforced(Boolean(res.data?.security_review_enforced));
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load sent documents."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  const openDocument = async (document) => {
    if (viewingId) return;
    setViewingId(document.id);
    setError("");
    try {
        const response = await viewAttorneyDocument(document.id);
        const blob = new Blob([response.data], { type: "application/pdf" });
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, "_blank", "noopener,noreferrer");

        setTimeout(() => {
          URL.revokeObjectURL(objectUrl);
        }, 60000);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to open this document securely."));
    } finally {
      setViewingId(null);
    }
  };

  return (
    <div className="app-shell">
      <div className="topbar d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <div className="brand-mark">CV</div>

          <div>
            <h5 className="mb-0">CounselVault</h5>
            <div className="small opacity-75">Sent Document History</div>
          </div>
        </div>
	<AttorneyNav />
      </div>

      <main className="page-wrap">
        <StatusMessage type="warning">{error}</StatusMessage>
        <div className="d-flex justify-content-between align-items-end mb-4">
          <div>
            <h2 className="mb-1">Sent Documents</h2>
            <p className="text-muted mb-0">
	      Documents you submitted to approved inmates.
            </p>
          </div>

          <button
            className="btn btn-dark"
            onClick={() => {
              window.location.href = "/attorney/upload";
            }}
          >
            Upload Document
          </button>
        </div>

        <div className="portal-card">
          <div className="portal-card-header d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0">Sent to Inmates</h5>
              <div className="text-muted small">
	         Documents you sent through approved facility/client relationships.
              </div>
            </div>

            <button className="btn btn-sm btn-outline-secondary" onClick={loadDocuments}>
              Refresh
            </button>
          </div>

          <div className="portal-card-body">
            {loading ? (
              <p className="mb-0">Loading documents...</p>
            ) : documents.length === 0 ? (
              <div className="text-center py-5">
                <h5>No sent documents yet</h5>
                <p className="text-muted mb-4">
		    Send a privileged legal document after your facility and client access are approved.
                </p>

                <button
                  className="btn btn-dark"
                  onClick={() => {
                    window.location.href = "/attorney/upload";
                  }}
                >
                  Upload First Document
                </button>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>Client / Inmate</th>
                      <th>Facility</th>
                      <th>Status</th>
                      <th>Uploaded</th>
                      <th>Updated</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {documents.map((document) => (
                      <tr key={document.id}>
                        <td>
                          <strong>{document.title}</strong>
                          <div className="text-muted small">
                            {document.original_filename || "PDF document"}
                          </div>
                        </td>

                        <td>
                          {document.inmate ? (
                            <>
                              <strong>
                                {document.inmate.first_name} {document.inmate.last_name}
                              </strong>
                              <div className="text-muted small">
                                #{document.inmate.inmate_number}
                              </div>
                            </>
                          ) : (
                            "—"
                          )}
                        </td>

                        <td>
                          <div>{document.facility?.name || "—"}</div>
                          <div className="text-muted small">
                            {document.facility?.code || ""}
                          </div>
                        </td>

			<td>
			  <span className={`status-pill ${statusClass(document.status)}`}>
			    {formatStatus(document.status)}
			  </span>

			  {document.status === "rejected" && document.rejection_reason && (
			    <div
			      className="text-muted small mt-2 text-truncate"
			      style={{ maxWidth: "260px" }}
			      title={document.rejection_reason}
			    >      
			      <strong>Reason:</strong> {document.rejection_reason}
			    </div>
			  )}
			  <div className="text-muted small mt-1">{securityReviewEnforced ? scanStatus(document.malware_scan_status) : "Malware-scan enforcement disabled"}</div>
			</td>

                        <td>{formatDate(document.created_at)}</td>
                        <td>{formatDate(document.updated_at)}</td>

                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-outline-dark"
                            onClick={() => openDocument(document)}
                            disabled={viewingId !== null || (securityReviewEnforced && !["clean", "bypassed"].includes(document.malware_scan_status))}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
